/**
 * Markdown text, rendered with the theme's typography.
 *
 * For short text people write for each other — a notice, a description — not
 * for documents. CommonMark through `react-markdown`, which builds React
 * elements and never sets HTML, with three decisions on top:
 *
 * - **Raw HTML is dropped** (`skipHtml`), not escaped onto the screen. The text
 *   comes from users; a `<script>` in it must do nothing, and a `<b>` showing
 *   as literal angle brackets helps nobody.
 * - **No images.** An image is a request to whatever host the writer chose, made
 *   by every reader's browser — a tracker in a notice. `![…](…)` renders nothing.
 * - **Links open in a new tab**, with `noopener noreferrer`. `react-markdown`'s
 *   default URL filter already turns `javascript:` and other unsafe schemes
 *   into nothing.
 *
 * Headings start one size below the page's own, so `# Title` inside a panel
 * does not outrank the panel's title.
 */
import type { ReactNode } from 'react';
import Markdown, { type Components } from 'react-markdown';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

export interface MarkdownTextProps {
  /** The Markdown source. */
  children: string;
}

function heading(variant: 'h6' | 'subtitle1' | 'subtitle2') {
  return function Heading({ children }: { children?: ReactNode }) {
    return (
      <Typography variant={variant} component="p" sx={{ fontWeight: 600, mt: 2, mb: 1, '&:first-of-type': { mt: 0 } }}>
        {children}
      </Typography>
    );
  };
}

const COMPONENTS: Components = {
  h1: heading('h6'),
  h2: heading('subtitle1'),
  h3: heading('subtitle2'),
  h4: heading('subtitle2'),
  h5: heading('subtitle2'),
  h6: heading('subtitle2'),
  p: ({ children }) => (
    <Typography variant="body2" sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
      {children}
    </Typography>
  ),
  a: ({ href, children }) => (
    <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover">
      {children}
    </Link>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ typography: 'body2', pl: 3, mt: 0, mb: 1.5, '&:last-child': { mb: 0 } }}>
      {children}
    </Box>
  ),
  ol: ({ children, start }) => (
    <Box component="ol" start={start} sx={{ typography: 'body2', pl: 3, mt: 0, mb: 1.5, '&:last-child': { mb: 0 } }}>
      {children}
    </Box>
  ),
  blockquote: ({ children }) => (
    <Box
      component="blockquote"
      sx={{ borderLeft: 3, borderColor: 'divider', color: 'text.secondary', pl: 2, mx: 0, my: 1.5 }}
    >
      {children}
    </Box>
  ),
  code: ({ children }) => (
    <Box
      component="code"
      sx={{ fontFamily: 'monospace', fontSize: '0.875em', bgcolor: 'action.hover', borderRadius: 0.5, px: 0.5 }}
    >
      {children}
    </Box>
  ),
  pre: ({ children }) => (
    <Box
      component="pre"
      sx={{
        bgcolor: 'action.hover',
        borderRadius: 1,
        p: 1.5,
        my: 1.5,
        overflowX: 'auto',
        '& code': { bgcolor: 'transparent', p: 0 },
      }}
    >
      {children}
    </Box>
  ),
  hr: () => <Divider sx={{ my: 2 }} />,
};

export function MarkdownText({ children }: MarkdownTextProps) {
  return (
    <Box sx={{ overflowWrap: 'anywhere' }}>
      <Markdown components={COMPONENTS} skipHtml disallowedElements={['img']} unwrapDisallowed>
        {children}
      </Markdown>
    </Box>
  );
}
